import { DepositType } from '../../core/types/enums';
import { JsonProperty } from '../../core/services/utils/mapping.service';
import { Category } from './category.model';
import { Entity } from './entity.model';
import { Resident } from './resident.model';

const getDepositAmountFromType = (depositType: DepositType): number => {
  switch (depositType) {
    case DepositType.MEHRWEG_0_15:
      return 0.15;
    case DepositType.EINWEG_0_25:
      return 0.25;
    case DepositType.MEHRWEG_0_08:
      return 0.08;
    default:
      return 0;
  }
};

const calculateDepositForProducts = (products: Array<CartProduct>): number =>
  parseFloat(
    products
      .reduce(
        (prev, curr) =>
          prev +
          getDepositAmountFromType(curr.depositType) * (curr.quantity ?? 0),
        0
      )
      .toFixed(2)
  );

export class CartProductCategory {
  @JsonProperty('name')
  name: string = '';
  @JsonProperty('categoryId')
  categoryId: number = 0;
  @JsonProperty('categorySort')
  categorySort: number = 0;
}

export class CartProduct extends Entity {
  @JsonProperty('brand')
  brand: string = '';
  @JsonProperty('category')
  category: string = '';
  @JsonProperty({ clazz: CartProductCategory, name: 'categories' })
  categories: Array<CartProductCategory> = [];
  @JsonProperty('price')
  price: number = 0;
  @JsonProperty('quantity')
  quantity: number = 0;
  @JsonProperty('total')
  total: number = 0;
  @JsonProperty('sort')
  sort: number = 0;
  @JsonProperty()
  customerProductId: number = 0;
  @JsonProperty('depositType')
  depositType: DepositType = DepositType.NONE;
  @JsonProperty()
  categoryId: number = 0;
  @JsonProperty()
  inSortiment: number = 0;
  @JsonProperty('imageUrl')
  imageUrl: string = '';

  constructor(init?: Partial<CartProduct>) {
    super();
    Object.assign(this, init);
  }

  recalculateCartProductTotal(quantity): number {
    return this.price * quantity;
  }

  changeProductQuantity(quantity) {
    this.quantity = quantity;
    this.total = this.recalculateCartProductTotal(quantity);
  }
}

export class ResidentCart {
  @JsonProperty('deposit')
  deposit: number = 0;
  @JsonProperty({ clazz: CartProduct, name: 'products' })
  products: Array<CartProduct> = [];
  @JsonProperty('total')
  total: number = 0;
  @JsonProperty()
  residentId: number = 0;
  @JsonProperty('resident')
  resident?: Resident = null;

  constructor(init?: Partial<ResidentCart>) {
    Object.assign(this, init);
  }

  addProduct(product: CartProduct) {
    if (!this.products) {
      this.products = [product];
    } else {
      this.products.push(product);
    }
    this.total = this.recalculateResidentCartTotal();
    this.deposit = calculateDepositForProducts(this.products);
  }

  removeProduct(productId: number) {
    this.products = this.products.filter(x => x.id !== productId);
    this.total = this.recalculateResidentCartTotal();
    this.deposit = calculateDepositForProducts(this.products);
  }

  getProduct(productId: number): CartProduct | undefined {
    return this.products.find(p => p.id === productId);
  }

  changeProductQuantity(productId: number, quantity: number) {
    const product = this.getProduct(productId);
    if (product) {
      product.changeProductQuantity(quantity);
      this.total = this.recalculateResidentCartTotal();
      this.deposit = calculateDepositForProducts(this.products);
    }
  }

  recalculateResidentCartTotal(): number {
    return this.products.reduce((prev, curr) => {
      const sumToAdd = parseFloat(curr.total.toFixed(2));
      return parseFloat((prev + sumToAdd).toFixed(2));
    }, 0);
  }

  get getTotalItemsCount(): number {
    return this.products.reduce((prev, curr) => {
      return prev + curr.quantity;
    }, 0);
  }

  sortCartProductsPerResident(categories: Array<Category>) {
    const products = this.products.slice();
    products.sort((prevProduct, nextProduct) => {
      const prev = categories.find(
        category => +category.id === +prevProduct.categoryId
      );
      const next = categories.find(
        category => +category.id === +nextProduct.categoryId
      );
      const prevProductSort = prev ? prev.sort : -1;
      const nextProductSort = next ? next.sort : -1;

      if (prevProductSort > nextProductSort) {
        return 1;
      }
      if (prevProductSort < nextProductSort) {
        return -1;
      }
      return 0;
    });
    return products;
  }

  emptyCart() {
    this.products = [];
    this.total = 0;
    this.deposit = 0;
  }
}

export class Cart {
  @JsonProperty()
  departmentId: number = 0;
  @JsonProperty()
  cartId: number = 0;
  @JsonProperty()
  customerId: number = 0;
  @JsonProperty('total')
  total: number = 0;
  @JsonProperty({ clazz: ResidentCart, name: 'carts' })
  residentCarts: Array<ResidentCart> = [];
  @JsonProperty()
  totalItemsCount: number = 0;
  @JsonProperty()
  cartTotal: number = 0;
  @JsonProperty()
  saveDate: string = new Date().toISOString();
  @JsonProperty()
  lastModifiedBy: string = '';
  @JsonProperty()
  departmentName: string = '';

  get isCartEmpty() {
    if (!this.residentCarts) return true;
    return this.residentCarts.length < 1;
  }

  constructor(init?: Partial<Cart>) {
    Object.assign(this, {
      ...init,
      residentCarts:
        init?.residentCarts?.map(
          rc =>
            new ResidentCart({
              ...rc,
              products: rc.products.map(p => new CartProduct({ ...p })),
            })
        ) || [],
    });
  }

  recalculateCartTotal(): number {
    return this.residentCarts.reduce((prev, curr) => {
      curr.total = curr.recalculateResidentCartTotal();
      curr.deposit = calculateDepositForProducts(curr.products);
      const sumToAdd = parseFloat(curr.total.toFixed(2));
      return parseFloat((prev + sumToAdd).toFixed(2));
    }, 0);
  }

  getTotalItemsCount(): number {
    return this.residentCarts.reduce((prev, curr) => {
      return prev + curr.getTotalItemsCount;
    }, 0);
  }

  addProductToResidentCart(residentId: number, product: CartProduct) {
    let residentCart = this.getResidentCart(residentId);
    if (!residentCart) {
      residentCart = new ResidentCart({ residentId });
    }
    residentCart.addProduct(product);
    this.total = this.recalculateCartTotal();
    this.totalItemsCount = this.getTotalItemsCount();
  }

  removeProductInResidentCart(residentId: number, productId: number) {
    let residentCart = this.getResidentCart(residentId);
    if (!residentCart) {
      return;
    }
    residentCart.removeProduct(productId);
    this.total = this.recalculateCartTotal();
    this.totalItemsCount = this.getTotalItemsCount();
  }

  changeResidentCartProductQuantity(
    residentId: number,
    productId: number,
    quantity: number
  ) {
    let residentCart = this.getResidentCart(residentId);
    if (residentCart) {
      residentCart.changeProductQuantity(productId, quantity);
    }

    this.total = this.recalculateCartTotal();
    this.totalItemsCount = this.getTotalItemsCount();
  }

  getResidentCart(residentId: number): ResidentCart | undefined {
    const residentCart = this.residentCarts.find(
      r => r.residentId === residentId
    );
    return residentCart;
  }

  createNewResidentCart(residentId: number): ResidentCart | undefined {
    const newResidentCart = new ResidentCart({ residentId });
    this.residentCarts.push(newResidentCart);
    return this.residentCarts.find(r => r.residentId === residentId);
  }

  convertCart(categories?: Array<Category>) {
    const cart = Object.assign({}, this) as any;
    cart.carts = this.residentCarts.map(residentCart => {
      delete residentCart.resident;
      const newResidentCart = { ...residentCart };
      newResidentCart.products = residentCart.products.map(product => {
        const newProduct = new CartProduct();
        Object.assign(newProduct, product);
        delete newProduct.categories;
        return newProduct;
      });

      return newResidentCart;
    });
    const residentCarts = cart.residentCarts;
    for (let index = 0; index < residentCarts.length; index++) {
      const residentCart = residentCarts[index];
      residentCart.total = residentCarts[index].total;
      residentCart.products = categories
        ? residentCarts[index].sortCartProductsPerResident(categories)
        : residentCarts[index].products;
    }
    cart.total = this.recalculateCartTotal();
    cart.saveDate = new Date().toISOString();
    delete cart.residentCarts;
    delete cart.departmentName;
    return cart;
  }

  emptyResidentCart(residentId: number): void {
    const residentCart = this.getResidentCart(residentId);
    residentCart?.emptyCart();
    this.total = this.recalculateCartTotal();
    this.totalItemsCount = this.getTotalItemsCount();
  }

  emptyEntireCart(): void {
    this.residentCarts.forEach(residentCart => residentCart.emptyCart());
    this.total = 0;
    this.totalItemsCount = 0;
  }

  containSomeProducts() {
    return this.residentCarts.some(
      residentCart => residentCart.products.length > 0
    );
  }

  getAllProducts() {
    return this.residentCarts.reduce((acc, curr) => {
      const products = curr.products;
      acc = acc.concat(products);
      return acc;
    }, [] as CartProduct[]);
  }

  filterUnavailableProducts(
    residents: Resident[] = [],
    unavailableProductIds: number[] = []
  ): { product: CartProduct; resident: Resident }[] {
    const removedProducts: { product: CartProduct; resident: Resident }[] = [];
    this.residentCarts.forEach(residentCart => {
      residentCart.products = residentCart.products.filter(cartProduct => {
        if (
          !cartProduct.inSortiment ||
          unavailableProductIds.includes(cartProduct.id)
        ) {
          removedProducts.push({
            product: cartProduct,
            ...(residents.length > 0 && {
              resident: residents.find(r => r?.id === residentCart?.residentId),
            }),
          });
          return false;
        }
        return true;
      });
    });
    this.total = this.recalculateCartTotal();
    return removedProducts;
  }

  filterInactiveResidentsProducts(
    residents: Resident[] = []
  ): { product: CartProduct; resident: Resident }[] {
    const removedProducts: { product: CartProduct; resident: Resident }[] = [];
    this.residentCarts.forEach(residentCart => {
      residentCart.products = residentCart.products.filter(cartProduct => {
        if (residentCart.resident.active === 0) {
          removedProducts.push({
            product: cartProduct,
            ...(residents.length > 0 && {
              resident: residents.find(r => r.id === residentCart.residentId),
            }),
          });
          return false;
        }
        return true;
      });
    });
    this.total = this.recalculateCartTotal();
    return removedProducts;
  }

  filterDeletedResidents(existingResidentsIds: number[]): ResidentCart[] {
    const removedResidentCarts: ResidentCart[] = [];
    this.residentCarts = this.residentCarts.filter(residentCart => {
      if (existingResidentsIds.includes(residentCart.residentId)) return true;
      removedResidentCarts.push(residentCart);
      return false;
    });
    this.total = this.recalculateCartTotal();
    return removedResidentCarts;
  }
}

export class DeletedProducts {
  cart: Cart;
  department: {
    id: number;
    name: string;
    products: { product: CartProduct; resident: Resident }[];
  };
}
